import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
} from "@mui/material";
import { useDataProvider, useNotify, useRedirect } from "react-admin";

interface User {
  id: number;
  name: string;
  registration: string;
  email: string;
}

interface CreateLicenseDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CreateLicenseDialog = ({ open, onClose }: CreateLicenseDialogProps) => {
  const [usersByName, setUsersByName] = useState<User[]>([]);
  const [usersByRegistration, setUsersByRegistration] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [registrationInput, setRegistrationInput] = useState("");
  const [loadingName, setLoadingName] = useState(false);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const searchByName = async (searchText: string) => {
    if (searchText.trim().length < 2) {
      setUsersByName([]);
      return;
    }

    setLoadingName(true);
    try {
      const { data } = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { name: searchText },
      });
      setUsersByName(data as User[]);
    } catch (error) {
      console.error('Erro ao buscar usuários por nome:', error);
      setUsersByName([]);
    } finally {
      setLoadingName(false);
    }
  };

  const searchByRegistration = async (searchText: string) => {
    const normalizedSearch = searchText.toUpperCase();

    if (normalizedSearch.trim().length < 3) {
      setUsersByRegistration([]);
      return;
    }

    setLoadingRegistration(true);
    try {
      const { data } = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { registration: normalizedSearch },
      });
      setUsersByRegistration(data as User[]);
    } catch (error) {
      console.error('Erro ao buscar usuários por CPF/CNPJ:', error);
      setUsersByRegistration([]);
    } finally {
      setLoadingRegistration(false);
    }
  };

  const handleNameInputChange = (_event: any, value: string) => {
    setNameInput(value);
    searchByName(value);
  };

  const handleRegistrationInputChange = (_event: any, value: string) => {
    const normalizedValue = value.toUpperCase();
    setRegistrationInput(normalizedValue);
    searchByRegistration(normalizedValue);
  };

  const handleCreate = () => {
    if (!selectedUser) {
      notify('Selecione um usuário antes de continuar', { type: 'warning' });
      return;
    }

    // Armazena o userId temporariamente no localStorage
    localStorage.setItem('selectedUserId', selectedUser.id.toString());
    
    // Redireciona para a página de criação
    redirect('/licenses/create');
    onClose();
    resetDialog();
  };

  const resetDialog = () => {
    setSelectedUser(null);
    setNameInput("");
    setRegistrationInput("");
    setUsersByName([]);
    setUsersByRegistration([]);
  };

  const handleClose = () => {
    onClose();
    resetDialog();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Selecionar Usuário para Nova Licença</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Selecione o usuário utilizando uma das opções abaixo:
          </Typography>

          <Autocomplete
            options={usersByName}
            getOptionLabel={(option: User) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingName}
            value={selectedUser}
            onChange={(_event, newValue) => {
              setSelectedUser(newValue);
              if (newValue) {
                setRegistrationInput("");
              }
            }}
            inputValue={nameInput}
            onInputChange={handleNameInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar por Nome"
                margin="normal"
                fullWidth
                helperText="Digite pelo menos 2 caracteres"
              />
            )}
            noOptionsText="Nenhum usuário encontrado"
            loadingText="Buscando..."
          />

          <Autocomplete
            options={usersByRegistration}
            getOptionLabel={(option: User) => `${option.registration} - ${option.name}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingRegistration}
            value={selectedUser}
            onChange={(_event, newValue) => {
              setSelectedUser(newValue);
              if (newValue) {
                setNameInput("");
              }
            }}
            inputValue={registrationInput}
            onInputChange={handleRegistrationInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar por CPF/CNPJ"
                margin="normal"
                fullWidth
                helperText="Digite pelo menos 3 caracteres"
              />
            )}
            noOptionsText="Nenhum usuário encontrado"
            loadingText="Buscando..."
          />

          {selectedUser && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Usuário Selecionado:
              </Typography>
              <Typography variant="body2">
                <strong>Nome:</strong> {selectedUser.name}
              </Typography>
              <Typography variant="body2">
                <strong>CPF/CNPJ:</strong> {selectedUser.registration}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={!selectedUser}
        >
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
